import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert, Tag } from 'antd';
import {
  Calendar,
  Check,
  ListChecks,
} from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import COLORS from '../../../util/constants/colors';
import {
  changedFollowUpFields,
  assigneeOptionLabel,
  selectOptions,
} from './callFollowUpFormUtils';
import useCallFollowUpEditor from './useCallFollowUpEditor';
import './callFollowUps.scss';
import {
  AddEntryForm,
  FollowUpField,
  FollowUpFormFields,
  FollowUpOwnerField,
  formatEntryCreator,
  formatEntryDate,
} from './ExistingFollowUpEditorPrimitives';

const InboundExistingFollowUpEditorView = ({
  followup,
  saving,
  saveError,
  editMode,
  setEditMode,
  editForm,
  entryForm,
  setEntryForm,
  assigneeOptions,
  statusOptions,
  dispositionOptions,
  controlsDisabled,
  detailsDirty,
  cancelEdit,
  updateDetails,
  updateEditFormField,
  addEntry,
}) => {
  const renderField = (icon, label, value) => (
    <FollowUpField
      className="call-followup-field"
      labelClassName="call-followup-field-label"
      icon={icon}
      label={label}
      value={value}
    />
  );

  const renderCurrentState = () => (
    <div className="call-followup-current-state">
      <FollowUpOwnerField
        followup={followup}
        className="call-followup-field"
        labelClassName="call-followup-field-label"
      />
      {renderField(<Calendar size={16} />, 'Due date', followup.due_date)}
      {renderField(<Check size={16} />, 'Final outcome', followup.call_disposition?.name)}
      {renderField(<ListChecks size={16} />, 'Current status', followup.current_status?.name)}
    </div>
  );

  const renderEditControls = () => {
    if (!editMode) {
      return (
        <div className="call-followup-actions">
          <Button type="secondary" size="small" label="Edit details" onClick={() => setEditMode(true)} disabled={controlsDisabled} />
        </div>
      );
    }

    return (
      <div className="call-followup-edit">
        <FollowUpFormFields
          form={editForm}
          formGridClassName="call-followup-form-grid"
          inputClassName="call-followup-input"
          assigneeOptions={assigneeOptions}
          dispositionOptions={dispositionOptions}
          controlsDisabled={controlsDisabled}
          updateField={updateEditFormField}
        />
        <div className="call-followup-actions">
          <Button type="secondary" size="small" label="Cancel" onClick={cancelEdit} disabled={saving} />
          <Button type="primary" size="small" label="Save changes" onClick={updateDetails} disabled={controlsDisabled || !detailsDirty} />
        </div>
      </div>
    );
  };

  const renderEntry = (entry) => (
    <div className="call-followup-entry" key={entry.id}>
      <div className="call-followup-entry-main">
        <div>
          <Tag className="follow-up-entry-status-tag">{entry.call_followup_status?.name || 'Status'}</Tag>
          <Typography variant="text" size="xsmall" color={COLORS.TEXT_TERTIARY_LITE}>
            {`Created by ${formatEntryCreator(entry)}${formatEntryDate(entry.created_at) ? ` - ${formatEntryDate(entry.created_at)}` : ''}`}
          </Typography>
        </div>
        {entry.note && <Typography variant="text" size="small">{entry.note}</Typography>}
      </div>
    </div>
  );

  return (
    <div className="call-followup-detail" data-testid="call-followup-detail">
      {saveError && <Alert message={saveError} type="error" showIcon />}
      {renderEditControls()}
      {renderCurrentState()}
      <div className="call-followup-entries">
        <Typography variant="text" size="small" bold>Entries</Typography>
        {(followup.entries || []).map(renderEntry)}
      </div>
      <AddEntryForm
        addEntryClassName="call-followup-add-entry"
        addStatusGridClassName="call-followup-form-grid compact"
        actionsClassName="call-followup-actions"
        inputClassName="call-followup-input"
        noteInputClassName="call-followup-input call-followup-note"
        entryForm={entryForm}
        setEntryForm={setEntryForm}
        statusOptions={statusOptions}
        controlsDisabled={controlsDisabled}
        saving={saving}
        showCancel={false}
        submitLabel="Add entry"
        submitSize="small"
        onSubmit={addEntry}
      />
    </div>
  );
};

const InboundExistingFollowUpEditor = ({
  followup,
  options,
  optionsLoading,
  optionsError,
  onFollowUpChanged,
}) => {
  const editor = useCallFollowUpEditor({ followup, onFollowUpChanged, analyticsSurface: 'voice_inbound_panel' });
  const assigneeOptions = useMemo(
    () => selectOptions(options?.assignees, assigneeOptionLabel),
    [options],
  );
  const statusOptions = useMemo(() => selectOptions(options?.call_followup_statuses), [options]);
  const dispositionOptions = useMemo(() => selectOptions(options?.call_dispositions), [options]);
  const controlsDisabled = editor.saving || optionsLoading || !!optionsError || !options;
  const detailsDirty = Object.keys(changedFollowUpFields(editor.editForm, followup)).length > 0;

  useEffect(() => {
    const firstStatusId = options?.call_followup_statuses?.[0]?.id || null;
    if (!firstStatusId || editor.entryForm.statusId) return;

    editor.setEntryForm((form) => ({ ...form, statusId: firstStatusId }));
  }, [options?.call_followup_statuses, editor.entryForm.statusId, editor.setEntryForm]);

  const updateEditFormField = (field, value) => {
    editor.setEditForm({ ...editor.editForm, [field]: value });
  };

  return (
    <InboundExistingFollowUpEditorView
      followup={followup}
      saving={editor.saving}
      saveError={editor.saveError}
      editMode={editor.editMode}
      setEditMode={editor.setEditMode}
      editForm={editor.editForm}
      entryForm={editor.entryForm}
      setEntryForm={editor.setEntryForm}
      assigneeOptions={assigneeOptions}
      statusOptions={statusOptions}
      dispositionOptions={dispositionOptions}
      controlsDisabled={controlsDisabled}
      detailsDirty={detailsDirty}
      cancelEdit={editor.cancelEdit}
      updateDetails={editor.updateDetails}
      updateEditFormField={updateEditFormField}
      addEntry={editor.addEntry}
    />
  );
};

InboundExistingFollowUpEditorView.propTypes = {
  followup: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  saveError: PropTypes.string,
  editMode: PropTypes.bool.isRequired,
  setEditMode: PropTypes.func.isRequired,
  editForm: PropTypes.object.isRequired,
  entryForm: PropTypes.object.isRequired,
  setEntryForm: PropTypes.func.isRequired,
  assigneeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  dispositionOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  controlsDisabled: PropTypes.bool.isRequired,
  detailsDirty: PropTypes.bool.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  updateDetails: PropTypes.func.isRequired,
  updateEditFormField: PropTypes.func.isRequired,
  addEntry: PropTypes.func.isRequired,
};

InboundExistingFollowUpEditorView.defaultProps = {
  saveError: null,
};

InboundExistingFollowUpEditor.propTypes = {
  followup: PropTypes.object.isRequired,
  options: PropTypes.object,
  optionsLoading: PropTypes.bool,
  optionsError: PropTypes.string,
  onFollowUpChanged: PropTypes.func,
};

InboundExistingFollowUpEditor.defaultProps = {
  options: null,
  optionsLoading: false,
  optionsError: null,
  onFollowUpChanged: () => {},
};

export default InboundExistingFollowUpEditor;
